import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, Star } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaUpload } from "@/components/site/MediaUpload";
import { toast } from "sonner";

const PORTFOLIO_CATS = ["Skin Fade", "Mid Fade", "Low Fade", "High Fade", "Beard", "Line Up"];

export default function AdminMedia() {
  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Media & Reviews</h1>
      <Tabs defaultValue="portfolio" className="mt-6">
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="portfolio" className="mt-6"><PortfolioManager /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ReviewsManager /></TabsContent>
        <TabsContent value="categories" className="mt-6"><CategoriesManager /></TabsContent>
      </Tabs>
    </div>
  );
}

function PortfolioManager() {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ image: null, title: "", category: "Skin Fade" });
  const load = () => api.get("/admin/portfolio").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newItem.image) return toast.error("Upload an image first");
    await api.post("/admin/portfolio", newItem);
    setNewItem({ image: null, title: "", category: "Skin Fade" }); load(); toast.success("Photo added");
  };
  const del = async (id) => { await api.delete(`/admin/portfolio/${id}`); load(); };

  return (
    <div>
      <div className="rounded-lg border border-white/10 bg-zinc-900/40 p-5">
        <MediaUpload testid="portfolio-image-upload" label="Add Photo" value={newItem.image} onChange={(v) => setNewItem({ ...newItem, image: v })} kind="portfolio" />
        <div className="mt-3 flex gap-2">
          <Input placeholder="Title (optional)" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
          <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
            <SelectTrigger className="w-44 bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-white">{PORTFOLIO_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="add-portfolio-btn" onClick={add} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
        {items.map((p) => (
          <div key={p.id} className="group relative overflow-hidden rounded-lg border border-white/10">
            <img src={resolveImg(p.image)} alt={p.title} className="aspect-square w-full object-cover" />
            <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">{p.category}</span>
            <button onClick={() => del(p.id)} className="absolute right-1 top-1 rounded-full bg-red-500 p-1 opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3 text-white" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [nr, setNr] = useState({ name: "", text: "", rating: 5, avatar: null, is_approved: true, is_featured: false });
  const load = () => api.get("/admin/reviews").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => { if (!nr.name || !nr.text) return toast.error("Name and text required"); await api.post("/admin/reviews", nr); setNr({ name: "", text: "", rating: 5, avatar: null, is_approved: true, is_featured: false }); load(); };
  const patch = async (id, body) => { await api.patch(`/admin/reviews/${id}`, body); load(); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };

  return (
    <div>
      <div className="space-y-3 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input placeholder="Student name" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
          <Input type="number" min="1" max="5" placeholder="Rating" value={nr.rating} onChange={(e) => setNr({ ...nr, rating: parseInt(e.target.value) || 5 })} className="bg-zinc-900 border-white/10 text-white" />
        </div>
        <Textarea placeholder="Review text" value={nr.text} onChange={(e) => setNr({ ...nr, text: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
        <MediaUpload label="Avatar (optional)" value={nr.avatar} onChange={(v) => setNr({ ...nr, avatar: v })} kind="image" />
        <Button data-testid="add-review-btn" onClick={add} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Plus className="mr-1 h-4 w-4" /> Add Review</Button>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((r) => (
          <div key={r.id} className="rounded-lg border border-white/10 bg-zinc-900/40 p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{r.name} <span className="text-[#DCA846]">{"★".repeat(Math.round(r.rating))}</span></p>
                <p className="mt-1 text-sm text-zinc-400">{r.text}</p>
              </div>
              <button onClick={() => del(r.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex gap-6 text-sm">
              <label className="flex items-center gap-2"><Switch checked={!!r.is_approved} onCheckedChange={(v) => patch(r.id, { is_approved: v })} /> Approved</label>
              <label className="flex items-center gap-2"><Switch checked={!!r.is_featured} onCheckedChange={(v) => patch(r.id, { is_featured: v })} /> Featured</label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/categories").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; await api.post("/admin/categories", { name }); setName(""); load(); };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="max-w-lg">
      <div className="flex gap-2">
        <Input placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-900 border-white/10 text-white" />
        <Button data-testid="add-category-btn" onClick={add} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded border border-white/10 bg-zinc-900/40 px-4 py-2">
            <span>{c.name}</span>
            <button onClick={() => del(c.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
